import React, { useState } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Grid,
  TextField,
  Paper,
  Box,
  Button,
  IconButton,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';

interface MiscellaneousProps {
  data: {
    operatingEnvelope: {
      minimumAmbient: string;
      maximumAmbient: string;
      maxOutletTemp: string;
    };
    connections: string[];
    soundPowerLevel: string;
  };
  onChange: (subSection: string | null, field: string, value: any) => void;
}

const MiscellaneousSection: React.FC<MiscellaneousProps> = ({ data, onChange }) => {
  const handleConnectionChange = (index: number, value: string) => {
    const newConnections = [...data.connections];
    newConnections[index] = value;
    onChange('connections', 'connections', newConnections);
  };

  const addConnection = () => {
    const newConnections = [...data.connections, ''];
    onChange('connections', 'connections', newConnections);
  };

  const removeConnection = (index: number) => {
    const newConnections = data.connections.filter((_, i) => i !== index);
    onChange('connections', 'connections', newConnections);
  };

  return (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="h5">7. Miscellaneous</Typography>
      </AccordionSummary>
      <AccordionDetails>
        {/* Operating Envelope */}
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>Operating Envelope</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="Minimum ambient"
                value={data.operatingEnvelope.minimumAmbient}
                onChange={(e) => onChange('operatingEnvelope', 'minimumAmbient', e.target.value)}
                fullWidth
                margin="normal"
                className="blue-highlight"
                size="small"
                helperText="[°C] Limits of the heat pump operating envelope"
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="Maximum ambient"
                value={data.operatingEnvelope.maximumAmbient}
                onChange={(e) => onChange('operatingEnvelope', 'maximumAmbient', e.target.value)}
                fullWidth
                margin="normal"
                className="blue-highlight"
                size="small"
                helperText="[°C]"
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="Max outlet temp"
                value={data.operatingEnvelope.maxOutletTemp}
                onChange={(e) => onChange('operatingEnvelope', 'maxOutletTemp', e.target.value)}
                fullWidth
                margin="normal"
                className="blue-highlight"
                size="small"
                helperText="[°C]"
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Connections */}
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>Connections (List)</Typography>
          <Typography variant="body2" gutterBottom>
            Flow and return connections
          </Typography>
          
          {data.connections.map((connection, index) => (
            <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <TextField
                label={`Connection ${index + 1}`}
                value={connection}
                onChange={(e) => handleConnectionChange(index, e.target.value)}
                fullWidth
                margin="normal"
                className="blue-highlight"
                size="small"
              />
              {data.connections.length > 1 && (
                <IconButton 
                  color="error" 
                  onClick={() => removeConnection(index)}
                  sx={{ ml: 1 }}
                >
                  <DeleteIcon />
                </IconButton>
              )}
            </Box>
          ))}
          
          <Button 
            startIcon={<AddIcon />}
            variant="outlined"
            onClick={addConnection}
            size="small"
            sx={{ mt: 1 }}
          >
            Add Connection
          </Button>
        </Paper>

        {/* Sound Power Level */}
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>Misc</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Sound power level"
                value={data.soundPowerLevel}
                onChange={(e) => onChange(null, 'soundPowerLevel', e.target.value)}
                fullWidth
                margin="normal"
                className="blue-highlight"
                size="small"
                helperText="[dB] A-weighted, at 1 m"
              />
            </Grid>
          </Grid>
        </Paper>
      </AccordionDetails>
    </Accordion>
  );
};

export default MiscellaneousSection;